import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ArrowDown, ArrowUp, Bold, ImagePlus, Italic, PlusSquare, Trash2, Type, Underline } from "lucide-react";
import type {
  RichContentBlock,
  RichContentDocument,
  RichContentTextBlock,
  RichContentTextSegment,
} from "../../types/question-bank";
import { createRichContentDocument, createRichTextBlock, normalizeRichContentDocument } from "../../utils/questionRichContent";

type MathLikeAction = {
  mode: "insert" | "wrap";
  insert?: string;
  prefix?: string;
  suffix?: string;
  cursorOffset?: number;
};

export type RichContentEditorHandle = {
  focus: () => void;
  toggleBold: () => void;
  toggleItalic: () => void;
  toggleUnderline: () => void;
  applyMathAction: (action: MathLikeAction) => void;
};

type RichContentEditorProps = {
  value: RichContentDocument | null | undefined;
  onChange: (value: RichContentDocument) => void;
  placeholder?: string;
  allowImages?: boolean;
  onGenerateImage?: () => void;
  onUploadImage?: () => void;
  emptyImageText?: string;
};

const CARET_MARKER = "\u200B";

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const renderSegmentsAsHtml = (segments: RichContentTextSegment[]) =>
  segments
    .map((segment) => {
      const content = escapeHtml(segment.text).replace(/\n/g, "<br />");
      let html = content || "<br />";
      if (segment.bold) {
        html = `<strong>${html}</strong>`;
      }
      if (segment.italic) {
        html = `<em>${html}</em>`;
      }
      if (segment.underline) {
        html = `<u>${html}</u>`;
      }
      return html;
    })
    .join("");

const pushTextSegment = (segments: RichContentTextSegment[], text: string, bold: boolean, italic: boolean, underline: boolean) => {
  if (!text.length) return;
  const previous = segments[segments.length - 1];
  if (
    previous &&
    Boolean(previous.bold) === bold &&
    Boolean(previous.italic) === italic &&
    Boolean(previous.underline) === underline
  ) {
    previous.text += text;
    return;
  }
  segments.push({ text, bold: bold || undefined, italic: italic || undefined, underline: underline || undefined });
};

const parseTextBlockFromElement = (element: HTMLElement, currentBlock: RichContentTextBlock): RichContentTextBlock => {
  const nextSegments: RichContentTextSegment[] = [];

  const walk = (node: Node, bold: boolean, italic: boolean, underline: boolean) => {
    if (node.nodeType === Node.TEXT_NODE) {
      pushTextSegment(nextSegments, (node.textContent || "").split(CARET_MARKER).join(""), bold, italic, underline);
      return;
    }

    if (!(node instanceof HTMLElement)) {
      return;
    }

    const tagName = node.tagName.toLowerCase();
    if (tagName === "br") {
      pushTextSegment(nextSegments, "\n", bold, italic, underline);
      return;
    }

    const nextBold = bold || tagName === "strong" || tagName === "b";
    const nextItalic =
      italic ||
      tagName === "em" ||
      tagName === "i" ||
      node.style.fontStyle === "italic";
    const nextUnderline =
      underline ||
      tagName === "u" ||
      tagName === "ins" ||
      node.style.textDecoration.includes("underline") ||
      node.style.textDecorationLine.includes("underline");
    Array.from(node.childNodes).forEach((child) => walk(child, nextBold, nextItalic, nextUnderline));
  };

  Array.from(element.childNodes).forEach((node) => walk(node, false, false, false));

  return {
    ...currentBlock,
    segments: nextSegments.length ? nextSegments : [{ text: "" }],
  };
};

const INLINE_UNDERLINE_RE = /__([^_\n]{1,120}?)__/g;

const isInlineMathTokenChar = (char: string) => /[A-Za-z0-9çğıöşüÇĞİÖŞÜπθαβγλμσ∞⁰¹²³⁴⁵⁶⁷⁸⁹₀₁₂₃₄₅₆₇₈₉]/u.test(char);

const findMatchingOpenParenthesis = (source: string, closeIndex: number) => {
  let depth = 0;
  for (let index = closeIndex; index >= 0; index -= 1) {
    if (source[index] === ")") depth += 1;
    else if (source[index] === "(") {
      depth -= 1;
      if (depth === 0) return index;
    }
  }
  return -1;
};

const findMatchingCloseParenthesis = (source: string, openIndex: number) => {
  let depth = 0;
  for (let index = openIndex; index < source.length; index += 1) {
    if (source[index] === "(") depth += 1;
    else if (source[index] === ")") {
      depth -= 1;
      if (depth === 0) return index;
    }
  }
  return -1;
};

const stripWrappingParentheses = (value: string) => {
  let normalized = value.trim();
  let changed = true;

  while (changed && normalized.startsWith("(") && normalized.endsWith(")")) {
    changed = false;
    const closeIndex = findMatchingCloseParenthesis(normalized, 0);
    if (closeIndex === normalized.length - 1) {
      normalized = normalized.slice(1, -1).trim();
      changed = true;
    }
  }

  return normalized;
};

const readFractionNumerator = (source: string, slashIndex: number) => {
  let end = slashIndex;
  while (end > 0 && /\s/u.test(source[end - 1])) end -= 1;
  if (end <= 0) return null;

  let start = end;
  if (source[end - 1] === ")") {
    const openIndex = findMatchingOpenParenthesis(source, end - 1);
    if (openIndex === -1) return null;
    start = openIndex;
    if (source[start - 1] === "-") start -= 1;
  } else {
    while (start > 0 && isInlineMathTokenChar(source[start - 1])) start -= 1;
    if (source[start - 1] === "-") start -= 1;
  }

  const value = source.slice(start, end).trim();
  return value ? { start, end, value } : null;
};

const readFractionDenominator = (source: string, slashIndex: number) => {
  let start = slashIndex + 1;
  while (start < source.length && /\s/u.test(source[start])) start += 1;
  if (start >= source.length) return null;

  let end = start;
  if (source[start] === "(") {
    const closeIndex = findMatchingCloseParenthesis(source, start);
    if (closeIndex === -1) return null;
    end = closeIndex + 1;
  } else {
    if (source[end] === "-") end += 1;
    while (end < source.length && isInlineMathTokenChar(source[end])) end += 1;
  }

  const value = source.slice(start, end).trim();
  return value ? { start, end, value } : null;
};

const findInlineFractions = (text: string) => {
  const fractions: Array<{ num: string; den: string; start: number; end: number }> = [];

  for (let index = 0; index < text.length; index += 1) {
    if (text[index] !== "/" || text[index - 1] === "/" || text[index + 1] === "/") continue;

    const numerator = readFractionNumerator(text, index);
    const denominator = readFractionDenominator(text, index);
    if (!numerator || !denominator) continue;

    const raw = text.slice(numerator.start, denominator.end);
    if (!/[A-Za-z0-9çğıöşüÇĞİÖŞÜ()+\-−=]/u.test(raw)) continue;

    fractions.push({
      num: stripWrappingParentheses(numerator.value),
      den: stripWrappingParentheses(denominator.value),
      start: numerator.start,
      end: denominator.end,
    });
    index = denominator.end - 1;
  }

  return fractions;
};

const rangesOverlap = (startA: number, endA: number, startB: number, endB: number) => startA < endB && startB < endA;

// Altı çizili içeriğin içindeki bozuk parantezleri temizler: (((en)))) yüksek → en yüksek
const cleanUnderlineContent = (inner: string): string => {
  let s = inner;
  s = s.replace(/^\(+([^)]+?)\)+(?=\s|$)/g, "$1"); // (((en)))) rest → en rest
  s = s.replace(/^\(+([^)]+)\)+$/g, "$1");          // ((tüm içerik)) → tüm içerik
  s = s.replace(/^\(+/, "").replace(/\)+$/, "");    // artık baş/son parantezler
  return s.trim();
};

// Metindeki __altıçizili__ ve A/(B) kesir kalıplarını görsel bileşenlere dönüştürür
const renderLineWithFormatting = (text: string, lineKey: string): React.ReactNode => {
  type Chunk =
    | { kind: "underline"; value: string; start: number; end: number }
    | { kind: "fraction"; num: string; den: string; start: number; end: number };

  const chunks: Chunk[] = [];

  // Önce __...__  işaretlerini bul
  INLINE_UNDERLINE_RE.lastIndex = 0;
  let ulMatch: RegExpExecArray | null;
  while ((ulMatch = INLINE_UNDERLINE_RE.exec(text)) !== null) {
    chunks.push({
      kind: "underline",
      value: cleanUnderlineContent(ulMatch[1]),
      start: ulMatch.index,
      end: ulMatch.index + ulMatch[0].length,
    });
  }

  // Sonra kesir kalıplarını bul (underline ile çakışmayan konumlarda)
  for (const fraction of findInlineFractions(text)) {
    const overlapWithUnderline = chunks.some((chunk) => chunk.kind === "underline" && rangesOverlap(fraction.start, fraction.end, chunk.start, chunk.end));
    if (!overlapWithUnderline) {
      chunks.push({
        kind: "fraction",
        num: fraction.num,
        den: fraction.den,
        start: fraction.start,
        end: fraction.end,
      });
    }
  }

  if (chunks.length === 0) return text;
  chunks.sort((a, b) => a.start - b.start);

  const parts: React.ReactNode[] = [];
  let cursor = 0;

  for (const chunk of chunks) {
    if (chunk.kind === "underline") {
      if (chunk.start < cursor) continue;
      if (chunk.start > cursor) parts.push(text.slice(cursor, chunk.start));
      parts.push(<u key={`ul_${lineKey}_${chunk.start}`}>{chunk.value}</u>);
      cursor = chunk.end;
    } else if (chunk.kind === "fraction") {
      if (chunk.start < cursor) continue;
      if (chunk.start > cursor) parts.push(text.slice(cursor, chunk.start));
      parts.push(
        <span
          key={`frac_${lineKey}_${chunk.start}`}
          style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", fontSize: "0.82em", lineHeight: 1.25, verticalAlign: "middle", margin: "0 1px" }}
        >
          <span style={{ borderBottom: "1px solid currentColor", padding: "0 2px", textAlign: "center" }}>{(chunk as { num: string }).num}</span>
          <span style={{ padding: "0 2px", textAlign: "center" }}>{(chunk as { den: string }).den}</span>
        </span>
      );
      cursor = chunk.end;
    }
  }

  if (cursor < text.length) parts.push(text.slice(cursor));
  return <React.Fragment key={lineKey}>{parts}</React.Fragment>;
};

const renderTextSegments = (segments: RichContentTextSegment[]) =>
  segments.map((segment, index) => {
    const lines = segment.text.split("\n");
    const children: React.ReactNode[] = [];
    lines.forEach((line, lineIndex) => {
      children.push(
        <React.Fragment key={`text_${index}_${lineIndex}`}>
          {renderLineWithFormatting(line, `${index}_${lineIndex}`)}
        </React.Fragment>
      );
      if (lineIndex < lines.length - 1) {
        children.push(<br key={`break_${index}_${lineIndex}`} />);
      }
    });

    let content: React.ReactNode = <React.Fragment key={`segment_${index}`}>{children}</React.Fragment>;
    if (segment.bold) {
      content = <strong key={`segment_${index}_bold`}>{content}</strong>;
    }
    if (segment.italic) {
      content = <em key={`segment_${index}_italic`}>{content}</em>;
    }
    if (segment.underline) {
      content = <u key={`segment_${index}_underline`}>{content}</u>;
    }
    return content;
  });

const moveBlock = (blocks: RichContentBlock[], index: number, direction: -1 | 1) => {
  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= blocks.length) {
    return blocks;
  }

  const nextBlocks = [...blocks];
  const [currentBlock] = nextBlocks.splice(index, 1);
  nextBlocks.splice(targetIndex, 0, currentBlock);
  return nextBlocks;
};

const insertTextAtSelection = (selection: Selection, text: string, caretOffset = text.length) => {
  if (!selection.rangeCount) {
    return false;
  }

  const range = selection.getRangeAt(0);
  range.deleteContents();
  const textNode = document.createTextNode(text);
  range.insertNode(textNode);

  const nextRange = document.createRange();
  nextRange.setStart(textNode, Math.max(0, Math.min(caretOffset, text.length)));
  nextRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(nextRange);
  return true;
};

const insertLineBreakAtSelection = (selection: Selection) => {
  if (!selection.rangeCount) {
    return false;
  }

  const range = selection.getRangeAt(0);
  range.deleteContents();

  const lineBreak = document.createElement("br");
  const markerNode = document.createTextNode(CARET_MARKER);
  range.insertNode(lineBreak);
  lineBreak.parentNode?.insertBefore(markerNode, lineBreak.nextSibling);

  const nextRange = document.createRange();
  nextRange.setStart(markerNode, markerNode.length);
  nextRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(nextRange);
  return true;
};

const getSelectionText = (selection: Selection) => {
  if (!selection.rangeCount) return "";
  return selection.getRangeAt(0).toString();
};

const focusBlockElement = (element: HTMLElement | null) => {
  if (!element) return;
  element.focus();
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

export const RichContentRenderer = ({
  value,
  textClassName = "text-sm leading-6 text-gray-800",
  imageClassName = "max-h-52 w-auto rounded-lg border border-gray-200 bg-white object-contain",
}: {
  value: RichContentDocument | null | undefined;
  textClassName?: string;
  imageClassName?: string;
}) => {
  const documentValue = useMemo(() => normalizeRichContentDocument(value), [value]);

  return (
    <div className="space-y-3">
      {documentValue.blocks.map((block) =>
        block.type === "image" ? (
          <div key={block.id} className="flex justify-center">
            <img
              src={`data:${block.image.mimeType};base64,${block.image.base64}`}
              alt={block.image.prompt || "İçerik görseli"}
              className={imageClassName}
            />
          </div>
        ) : (
          <div key={block.id} className={`${textClassName} whitespace-pre-wrap break-words`}>
            {renderTextSegments(block.segments)}
          </div>
        )
      )}
    </div>
  );
};

const RichContentEditor = forwardRef<RichContentEditorHandle, RichContentEditorProps>(function RichContentEditor(
  {
    value,
    onChange,
    placeholder = "Metni yazın",
    allowImages = false,
    onGenerateImage,
    onUploadImage,
    emptyImageText = "Henüz içerik görseli eklenmedi.",
  },
  ref
) {
  const documentValue = useMemo(() => normalizeRichContentDocument(value), [value]);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const textBlockRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const activeBlockIdRef = useRef<string | null>(null);
  const [focusedBlockId, setFocusedBlockId] = useState<string | null>(null);
  const [htmlByBlockId, setHtmlByBlockId] = useState<Record<string, string>>(() =>
    documentValue.blocks.reduce<Record<string, string>>((acc, block) => {
      if (block.type === "text") {
        acc[block.id] = renderSegmentsAsHtml(block.segments);
      }
      return acc;
    }, {})
  );

  useEffect(() => {
    setHtmlByBlockId((prev) =>
      documentValue.blocks.reduce<Record<string, string>>((acc, block) => {
        if (block.type === "text") {
          acc[block.id] =
            focusedBlockId === block.id && typeof prev[block.id] === "string"
              ? prev[block.id]
              : renderSegmentsAsHtml(block.segments);
        }
        return acc;
      }, {})
    );
  }, [documentValue, focusedBlockId]);

  useEffect(() => {
    documentValue.blocks.forEach((block) => {
      if (block.type !== "text") return;
      const element = textBlockRefs.current[block.id];
      const nextHtml = htmlByBlockId[block.id] ?? renderSegmentsAsHtml(block.segments);
      if (!element || document.activeElement === element) return;
      if (element.innerHTML !== nextHtml) {
        element.innerHTML = nextHtml;
      }
    });
  }, [documentValue, htmlByBlockId]);

  useEffect(() => {
    if (!documentValue.blocks.some((block) => block.type === "text" && block.id === activeBlockIdRef.current)) {
      const firstTextBlock = documentValue.blocks.find((block) => block.type === "text");
      activeBlockIdRef.current = firstTextBlock?.id || null;
      setFocusedBlockId(firstTextBlock?.id || null);
    }
  }, [documentValue]);

  const emitChange = (nextBlocks: RichContentBlock[]) => {
    onChange({
      version: 1,
      blocks: nextBlocks.length ? nextBlocks : createRichContentDocument("").blocks,
    });
  };

  const syncTextBlock = (blockId: string, element?: HTMLElement | null) => {
    const currentBlock = documentValue.blocks.find((block): block is RichContentTextBlock => block.type === "text" && block.id === blockId);
    const activeElement = element || textBlockRefs.current[blockId];

    if (!currentBlock || !activeElement) {
      return null;
    }

    const nextTextBlock = parseTextBlockFromElement(activeElement, currentBlock);
    emitChange(documentValue.blocks.map((block) => (block.type === "text" && block.id === blockId ? nextTextBlock : block)));
    return nextTextBlock;
  };

  const getActiveTextElement = () => {
    if (activeBlockIdRef.current && textBlockRefs.current[activeBlockIdRef.current]) {
      return textBlockRefs.current[activeBlockIdRef.current];
    }
    const firstTextBlock = documentValue.blocks.find((block) => block.type === "text");
    return firstTextBlock ? textBlockRefs.current[firstTextBlock.id] : null;
  };

  const toggleBold = () => {
    applyTextStyle("bold");
  };

  const toggleItalic = () => {
    applyTextStyle("italic");
  };

  const toggleUnderline = () => {
    applyTextStyle("underline");
  };

  const applyTextStyle = (command: "bold" | "italic" | "underline") => {
    const selection = window.getSelection();
    const editorRoot = rootRef.current;
    const activeElement = getActiveTextElement();
    if (!selection || !editorRoot || !activeElement) {
      return;
    }

    if (!selection.anchorNode || !editorRoot.contains(selection.anchorNode)) {
      focusBlockElement(activeElement);
    }

    (document as any).execCommand?.(command);
    const blockId = activeElement.dataset.blockId;
    if (!blockId) {
      return;
    }
    const nextHtml = activeElement.innerHTML || "";
    setHtmlByBlockId((prev) => ({ ...prev, [blockId]: nextHtml }));
    syncTextBlock(blockId, activeElement);
  };

  const applyMathAction = (action: MathLikeAction) => {
    const activeElement = getActiveTextElement();
    if (!activeElement) {
      return;
    }

    focusBlockElement(activeElement);

    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    const selectedText = getSelectionText(selection);
    let insertedText = "";
    let caretOffset = 0;

    if (action.mode === "wrap") {
      const prefix = action.prefix || "";
      const suffix = action.suffix || "";
      insertedText = `${prefix}${selectedText}${suffix}`;
      caretOffset = selectedText ? insertedText.length : prefix.length;
    } else {
      insertedText = action.insert || "";
      caretOffset = action.cursorOffset ?? insertedText.length;
    }

    if (!insertTextAtSelection(selection, insertedText, caretOffset)) {
      return;
    }

    const blockId = activeElement.dataset.blockId;
    if (!blockId) {
      return;
    }
    const nextHtml = activeElement.innerHTML || "";
    setHtmlByBlockId((prev) => ({ ...prev, [blockId]: nextHtml }));
    syncTextBlock(blockId, activeElement);
  };

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        focusBlockElement(getActiveTextElement());
      },
      toggleBold,
      toggleItalic,
      toggleUnderline,
      applyMathAction,
    }),
    [applyMathAction, getActiveTextElement, toggleBold, toggleItalic, toggleUnderline]
  );

  const addTextBlock = () => {
    const nextTextBlock = createRichTextBlock("");
    setHtmlByBlockId((prev) => ({ ...prev, [nextTextBlock.id]: "" }));
    emitChange([...documentValue.blocks, nextTextBlock]);
    window.requestAnimationFrame(() => focusBlockElement(textBlockRefs.current[nextTextBlock.id]));
  };

  return (
    <div ref={rootRef} className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={toggleBold}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          <Bold className="h-4 w-4" />
          Kalın
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={toggleItalic}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          <Italic className="h-4 w-4" />
          İtalik
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={toggleUnderline}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          <Underline className="h-4 w-4" />
          Altı Çizili
        </button>
        <button
          type="button"
          onClick={addTextBlock}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          <PlusSquare className="h-4 w-4" />
          Metin Bloğu
        </button>
        {allowImages ? (
          <>
            <button
              type="button"
              onClick={onGenerateImage}
              className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm text-indigo-700 hover:bg-indigo-100"
            >
              <ImagePlus className="h-4 w-4" />
              Görsel Oluştur
            </button>
            <button
              type="button"
              onClick={onUploadImage}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <ImagePlus className="h-4 w-4" />
              Görsel Yükle
            </button>
          </>
        ) : null}
      </div>

      <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/70 p-3">
        {documentValue.blocks.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500">
            {emptyImageText}
          </div>
        ) : null}

        {documentValue.blocks.map((block, index) => (
          <div key={block.id} className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">
                {block.type === "image" ? <ImagePlus className="h-4 w-4" /> : <Type className="h-4 w-4" />}
                {block.type === "image" ? "Görsel" : "Metin"}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => emitChange(moveBlock(documentValue.blocks, index, -1))}
                  disabled={index === 0}
                  className="rounded-md border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => emitChange(moveBlock(documentValue.blocks, index, 1))}
                  disabled={index === documentValue.blocks.length - 1}
                  className="rounded-md border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                >
                  <ArrowDown className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => emitChange(documentValue.blocks.filter((currentBlock) => currentBlock.id !== block.id))}
                  disabled={documentValue.blocks.length === 1 && block.type === "text"}
                  className="rounded-md border border-rose-200 p-1.5 text-rose-600 hover:bg-rose-50 disabled:opacity-40"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {block.type === "image" ? (
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                <img
                  src={`data:${block.image.mimeType};base64,${block.image.base64}`}
                  alt={block.image.prompt || "İçerik görseli"}
                  className="max-h-56 w-full object-contain"
                />
                <div className="border-t border-gray-200 bg-white px-3 py-2 text-xs text-gray-500">
                  {block.image.prompt || block.image.fileName || "İçerik görseli"}
                </div>
              </div>
            ) : (
              <div className="relative">
                {!block.segments.some((segment) => segment.text.length > 0) ? (
                  <div className="pointer-events-none absolute left-3 top-3 text-sm text-gray-400">{placeholder}</div>
                ) : null}
                <div
                  ref={(element) => {
                    textBlockRefs.current[block.id] = element;
                    if (element && document.activeElement !== element) {
                      const nextHtml = htmlByBlockId[block.id] ?? renderSegmentsAsHtml(block.segments);
                      if (element.innerHTML !== nextHtml) {
                        element.innerHTML = nextHtml;
                      }
                    }
                  }}
                  data-block-id={block.id}
                  contentEditable
                  suppressContentEditableWarning
                  onFocus={() => {
                    activeBlockIdRef.current = block.id;
                    setFocusedBlockId(block.id);
                  }}
                  onInput={(event) => {
                    const targetElement = event.currentTarget;
                    const nextHtml = targetElement.innerHTML || "";
                    setHtmlByBlockId((prev) => ({ ...prev, [block.id]: nextHtml }));
                    syncTextBlock(block.id, targetElement);
                  }}
                  onBlur={(event) => {
                    const targetElement = event.currentTarget;
                    const nextBlock = syncTextBlock(block.id, targetElement);
                    setFocusedBlockId((current) => (current === block.id ? null : current));
                    if (nextBlock) {
                      const sanitizedHtml = renderSegmentsAsHtml(nextBlock.segments);
                      setHtmlByBlockId((prev) => ({ ...prev, [block.id]: sanitizedHtml }));
                      if (targetElement.innerHTML !== sanitizedHtml) {
                        targetElement.innerHTML = sanitizedHtml;
                      }
                    }
                  }}
                  onPaste={(event) => {
                    event.preventDefault();
                    const targetElement = event.currentTarget;
                    const text = event.clipboardData.getData("text/plain");
                    if (!text) return;
                    const selection = window.getSelection();
                    if (!selection) return;
                    insertTextAtSelection(selection, text, text.length);
                    const nextHtml = targetElement.innerHTML || "";
                    setHtmlByBlockId((prev) => ({ ...prev, [block.id]: nextHtml }));
                    syncTextBlock(block.id, targetElement);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      const targetElement = event.currentTarget;
                      const selection = window.getSelection();
                      if (!selection) return;
                      insertLineBreakAtSelection(selection);
                      const nextHtml = targetElement.innerHTML || "";
                      setHtmlByBlockId((prev) => ({ ...prev, [block.id]: nextHtml }));
                      syncTextBlock(block.id, targetElement);
                    }
                  }}
                  className={`min-h-[88px] rounded-lg border px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap break-words outline-none ${
                    focusedBlockId === block.id ? "border-blue-300 bg-blue-50/30" : "border-gray-200"
                  }`}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

export default RichContentEditor;
